import { useState } from "react";
import { Button, Card, IconButton, SegmentedControl, TextButton, TextField } from "@/components/ui";
import type { Menu, MenuboardDraft, OptionGroup } from "@/lib/api/types";

/** 직접 입력 · 사진 추출 결과 확인 · 기존 메뉴판 수정이 모두 이 편집기를 쓴다. */
export function MenuboardEditor({
  initial,
  saving,
  submitLabel = "저장하기",
  onSubmit,
  onCancel,
}: {
  initial: MenuboardDraft;
  saving: boolean;
  submitLabel?: string;
  onSubmit: (draft: MenuboardDraft) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [menus, setMenus] = useState<Menu[]>(initial.menus);

  function updateMenu(id: string, patch: Partial<Menu>) {
    setMenus((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }

  function updateGroups(menuId: string, next: OptionGroup[]) {
    updateMenu(menuId, { option_groups: next });
  }

  function addMenu() {
    setMenus((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: "", price: 0, option_groups: [] },
    ]);
  }

  const canSubmit = name.trim().length > 0 && menus.some((m) => m.name.trim().length > 0);

  function handleSubmit() {
    // 이름이 빈 메뉴는 사용자가 추가만 하고 안 채운 줄이다 — 저장에서 뺀다.
    const cleaned = menus
      .filter((m) => m.name.trim().length > 0)
      .map((m) => ({ ...m, name: m.name.trim() }));
    onSubmit({ name: name.trim(), menus: cleaned });
  }

  return (
    <div className="flex flex-col gap-4">
      <TextField
        label="메뉴판 이름"
        placeholder="예: 우리동네카페"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      {menus.map((menu) => (
        <Card key={menu.id} padding={16}>
          <div className="flex items-end gap-2">
            <TextField
              label="메뉴명"
              style={{ flex: 1, minWidth: 0 }}
              value={menu.name}
              onChange={(e) => updateMenu(menu.id, { name: e.target.value })}
            />
            <TextField
              label="가격"
              inputMode="numeric"
              placeholder="0"
              style={{ width: 88, flex: "none" }}
              // 새 줄에 "0" 이 박혀 있으면 지우고 쓰는 한 동작이 매번 더 붙는다.
              // 값이 0 이면 빈 칸으로 두고 placeholder 가 단위를 알려 준다.
              value={menu.price === 0 ? "" : String(menu.price)}
              onChange={(e) => updateMenu(menu.id, { price: parsePrice(e.target.value) })}
            />
            <IconButton
              icon="close"
              aria-label={`${menu.name || "메뉴"} 삭제`}
              style={{ flex: "none", marginBottom: 8 }}
              onClick={() => setMenus((prev) => prev.filter((m) => m.id !== menu.id))}
            />
          </div>

          <OptionGroupList
            menuId={menu.id}
            groups={menu.option_groups}
            onChange={(next) => updateGroups(menu.id, next)}
          />
        </Card>
      ))}

      {menus.length === 0 && (
        <p className="text-body-2 text-label-alternative">
          아직 메뉴가 없어요. 아래에서 하나씩 추가해 주세요.
        </p>
      )}

      <Button variant="outlined" onClick={addMenu}>
        메뉴 추가하기
      </Button>

      <div className="flex gap-2">
        <Button onClick={handleSubmit} disabled={saving || !canSubmit} style={{ flex: 1 }}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="outlined" color="assistive" onClick={onCancel} disabled={saving}>
            취소
          </Button>
        )}
      </div>
    </div>
  );
}

function OptionGroupList({
  menuId,
  groups,
  onChange,
}: {
  menuId: string;
  groups: OptionGroup[];
  onChange: (next: OptionGroup[]) => void;
}) {
  function patch(id: string, next: Partial<OptionGroup>) {
    onChange(groups.map((g) => (g.id === id ? { ...g, ...next } : g)));
  }

  return (
    <div className="mt-3 flex flex-col gap-3 border-line-normal border-t pt-3">
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-2">
          <div className="flex items-end gap-2">
            <TextField
              label="옵션 이름"
              size="medium"
              placeholder="예: 온도"
              style={{ flex: 1, minWidth: 0 }}
              value={group.name}
              onChange={(e) => patch(group.id, { name: e.target.value })}
            />
            <IconButton
              icon="close"
              size="small"
              aria-label={`${group.name || "옵션"} 삭제`}
              style={{ flex: "none", marginBottom: 12 }}
              onClick={() => onChange(groups.filter((g) => g.id !== group.id))}
            />
          </div>
          <SegmentedControl
            size="small"
            items={[
              { value: "single", label: "하나만 고르기" },
              { value: "multi", label: "여러 개 고르기" },
            ]}
            value={group.type}
            onChange={(value) => patch(group.id, { type: value as OptionGroup["type"] })}
          />
          <TextField
            label="선택 항목"
            size="medium"
            helperText="쉼표로 구분해요. 예: 핫, 아이스"
            // ponytail: 쉼표 문자열은 blur 에서만 반영한다(uncontrolled). 매 키
            // 입력마다 배열로 파싱하면 "핫, " 의 트레일링 쉼표가 지워져 커서가 튄다.
            defaultValue={group.choices.map((c) => c.name).join(", ")}
            onBlur={(e) => patch(group.id, { choices: parseChoices(group, e.target.value) })}
          />
        </div>
      ))}
      <TextButton
        onClick={() =>
          onChange([
            ...groups,
            { id: `${menuId}-${crypto.randomUUID()}`, name: "", type: "single", choices: [] },
          ])
        }
      >
        옵션 추가하기
      </TextButton>
    </div>
  );
}

function parsePrice(raw: string): number {
  const price = Number.parseInt(raw, 10);
  return Number.isNaN(price) || price < 0 ? 0 : price;
}

/** 쉼표 목록 → 선택 항목. 같은 자리의 id 는 유지한다 — id 가 바뀌면 그 옵션을 고른
 * 과거 제출이 이력 추천에서 매칭되지 않는다. */
export function parseChoices(group: OptionGroup, raw: string): OptionGroup["choices"] {
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((name, index) => ({ id: group.choices[index]?.id ?? crypto.randomUUID(), name }));
}
